import { cn } from "@/lib/utils"
import { Card, CardContent, CardHeader } from "@/components/ui/card"

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  )
}

/**
 * Skeleton loader for dashboard stat cards (balance, PnL, win rate, etc.)
 */
function DashboardCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <Skeleton className="h-4 w-24 mb-2" /> {/* Label */}
        <Skeleton className="h-8 w-32 mb-1" /> {/* Value */}
        <Skeleton className="h-3 w-20" /> {/* Subtitle */}
      </CardContent>
    </Card>
  )
}

/**
 * Skeleton loader for forecast chart area
 */
function ChartSkeleton({ 
  height = '500px', 
  className 
}: { 
  height?: string; 
  className?: string 
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-48" /> {/* Title */}
          <Skeleton className="h-9 w-24" /> {/* Button */}
        </div>
      </CardHeader>
      <CardContent>
        <Skeleton className="w-full" style={{ height }} /> {/* Chart area */}
      </CardContent>
    </Card>
  )
}

/**
 * Generic table skeleton loader
 */
function TableSkeleton({ 
  rows = 3, 
  columns = 5, 
  className 
}: { 
  rows?: number; 
  columns?: number; 
  className?: string 
}) {
  return (
    <div className={cn("space-y-2", className)}>
      {/* Header row */}
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} className="h-4 flex-1" />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-6 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Specialized skeleton for ActivePosition component
 */
function PositionsSkeleton({ className }: { className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center mb-4">
          <Skeleton className="h-6 w-32" /> {/* Title */}
          <Skeleton className="h-9 w-24" /> {/* Button */}
        </div>
        {/* Tab buttons */}
        <div className="flex gap-2">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </CardHeader>
      <CardContent>
        <TableSkeleton rows={3} columns={6} />
      </CardContent>
    </Card>
  )
}

/**
 * Specialized skeleton for TradingHistory component
 */
function HistorySkeleton({ className }: { className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-40" /> {/* Title */}
          <Skeleton className="h-9 w-32" /> {/* Filter button */}
        </div>
      </CardHeader>
      <CardContent>
        <TableSkeleton rows={5} columns={7} />
        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <Skeleton className="h-4 w-32" /> {/* Page info */}
          <div className="flex gap-2">
            <Skeleton className="h-9 w-20" /> {/* Previous button */}
            <Skeleton className="h-9 w-20" /> {/* Next button */}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export { 
  Skeleton, 
  DashboardCardSkeleton, 
  ChartSkeleton, 
  TableSkeleton, 
  PositionsSkeleton, 
  HistorySkeleton 
};

// New dashboard-specific skeletons from DashboardSkeleton module
export {
  GridMetricsSkeleton,
  AutoTradingStatusSkeleton,
  ActivePositionsSkeleton,
  DashboardTopGridSkeleton,
  FullDashboardSkeleton,
} from '@/components/skeleton/DashboardSkeleton';
